import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Jc3Service } from 'app/modules/jc3/jc3.service';
import { customEmailValidator } from 'app/shared/email-validator';
import { ToastrService } from 'ngx-toastr';
import { FeedbackSuccessComponent } from '../feedback-success/feedback-success.component';

@Component({
    selector: 'app-feedback-form',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        MatIconModule,
        MatDialogModule,
    ],
    templateUrl: './feedback-form.component.html',
    styleUrl: './feedback-form.component.scss',
})
export class FeedbackFormComponent {
    selectedLanguage: string = 'en';
    form: FormGroup;
    @ViewChild('formContainer') formContainer!: ElementRef;
    isLoading: boolean = false;

    translations = {
        en: {
            header: 'Feedback on ESG Jumpstart Portal',
            intro: 'Please take a moment to share your feedback on the ESG Jumpstart Portal for SMEs. Your insights will help us enhance the portal to better serve your needs. We are particularly interested in:',
            points: {
                improvement:
                    '<span class="tw-font-bold">Area for improvement:</span> Let us know any challenges you faced or features you think could be improved.',
                content:
                    '<span class="tw-font-bold">Content Suggestions:</span> Share your ideas for new content or resources that would be beneficial for your business.',
                experience:
                    '<span class="tw-font-bold">Overall Experience:</span> Tell us about your overall experience using the portal and any additional comments you may have.',
            },
            thankYou:
                'Thank you for helping us create a more effective and user-friendly platform!',
            name: 'Name',
            email: 'Email Address',
            company: 'Institution / Company Name',
            industry: 'Type of Industry',
            industries: {
                services: 'Services',
                manufacturing: 'Manufacturing',
                agriculture: 'Agriculture',
                mining: 'Mining and Quarrying',
                construction: 'Construction',
            },
            feedback: 'Feedback / Suggestion',
            feedbackPlaceholder: 'Enter your feedback here...',
            consent: 'By clicking on "Submit", you agreed to our',
            privacy: 'Privacy Notice',
            termsConditions: 'Terms of Use',
            submit: 'Submit',
            error: {
                required: {
                    name: 'Name is required',
                    email: 'Email address is required',
                    company: 'Company name is required',
                    industry: 'Type of Industry selection is required',
                    feedback: 'Feedback required',
                    termsConditions: 'Terms & Conditions is required',
                },
                pattern: {
                    name: 'Please enter a valid name (letters, numbers, spaces, 3–100 characters)',
                    email: 'Please enter a valid email address (letters, numbers, ._- allowed, 6–254 characters)',
                    company:
                        'Please enter a valid company name (letters, numbers, &, , . - allowed, 3–100 characters)',
                    feedback:
                        'Please enter a valid feedback (letters, numbers, special characters allowed, 15-500 characters)',
                },
            },
        },
    };

    constructor(
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<FeedbackFormComponent>,
        private toastr: ToastrService,
        private jc3Service: Jc3Service,
        private fb: FormBuilder
    ) {}

    ngOnInit() {
        this.form = this.fb.group({
            name: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z0-9 ]+$/), // only letters, numbers, spaces
                    Validators.minLength(3),
                    Validators.maxLength(100),
                ],
            ],
            email: [
                '',
                [
                    Validators.required,
                    Validators.email, // Angular's built-in email format check
                    customEmailValidator(), // our stricter rules
                    Validators.maxLength(254),
                ],
            ],
            company: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z0-9&.,\- ]+$/),
                    Validators.minLength(3),
                    Validators.maxLength(100),
                ],
            ],
            industry: ['', [Validators.required]],
            feedback: [
                '',
                [
                    Validators.required,
                    Validators.pattern(
                        /^[A-Za-z0-9\s.,&\-\/()@#!?:;'\"%*_+=\[\]{}|<>$^`~]+$/
                    ), //letters,numbers,special characters
                    Validators.minLength(15),
                    Validators.maxLength(500),
                ],
            ],
            termsConditions: [false, Validators.requiredTrue],
        });
    }

    submitForm() {
        if (this.isLoading) return;
        if (this.form.invalid) {
            this.form.markAllAsTouched();

            // find first invalid field
            const firstInvalidControl: HTMLElement | null =
                this.formContainer.nativeElement.querySelector('.ng-invalid');

            if (firstInvalidControl) {
                // scroll into view
                firstInvalidControl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                // focus only if it's not a radio
                if (firstInvalidControl.getAttribute('type') !== 'radio') {
                    firstInvalidControl.focus();
                }
            }

            return;
        }

        this.isLoading = true;
        let data = this.form.value;
        let objReq = {
            name: data.name,
            email: data.email,
            companyName: data.company,
            typeOfIndustry: data.industry,
            termsConditions: data.termsConditions,
            feedback: data.feedback,
        };
        this.jc3Service.sendFeedback(objReq).subscribe({
            next: (res) => {
                this.dialogRef.close();
                this.dialog.open(FeedbackSuccessComponent, {
                    width: '100%',
                    maxWidth: '736px',
                    height: '320px',
                    panelClass: 'app-details-modal-container',
                    disableClose: false,
                });
            },

            error: (err) => {
                this.toastr.error(
                    err.error?.message || 'Something went wrong!'
                );
            },
        });
    }
}
